import React, { useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Checkbox from '@components/common/Formik/FormikCheckBox';
import Input from '@components/common/Formik/FormikInputField';
import PasswordInput from '@components/common/Formik/FormikPaswordInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import AccountBlockedModal from '@components/common/Modals/AccountBlockedModal/AccountBlockedModal';
import AuthenticationPageContext from '@components/lpc/Authentication/AuthenticationPageContext';
import LostPasswordLink from '@components/lpc/Authentication/LeftSide/Login/LostPasswordLink/LostPasswordLink';
import { Types } from '@components/lpc/Pot/CreatePotLayout/PoolsTunnel/PotTypeSelection/PotTypeSelection';
import { DoubleAuthenticationModel } from '@models/UserModel/DoubleAuthenticationModels/DoubleAuthenticationModel';
import { loginErrorType } from '@propTypes/errorTypes';
import AuthService from '@services/domain/Lpc/AuthService';
import { isInternalServerError } from '@services/http/Http';
import { getRoute, ROUTE } from '@services/http/Route';
import ObjectService from '@services/utils/ObjectService';
import { getItemFromStorage } from '@services/utils/StorageService';
import { signIn } from '@validations/userSchema';

import css from './LoginForm.module.scss';

type LoginFromProps = {
    setError: any;
    fromFakePot?: boolean;
    email?: string;
    potCreationType?: any;
    sendAnalytics?: any;
};
const LoginForm = ({ setError, fromFakePot, email, potCreationType, sendAnalytics }: LoginFromProps) => {
    const { t } = useTranslateLpc(['lpc-authentication', 'common']);
    const router = useRouter();
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState(false);
    const [query, setQuery] = useState(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [isAccountBlockedModalVisible, setIsAccountBlockedModalVisible] = useState<boolean>(false);

    const getInitialValue = () => {
        return { email: email || '', password: '', stayConnected: true };
    };

    const onLoginSuccess = (
        data,
        values,
        setPage,
        pages,
        setEmail,
        setPassword,
        setStayConnected,
        setPhone,
        setTfaType,
    ) => {
        if (data.needTfaActivation) {
            return router
                .push({ pathname: getRoute(ROUTE.LPC.AUTH.TFA), query: router.query + '&&autoCreate=true' })
                .then();
        }
        if (data.needSmsTfa || data.needGoogleTfa || data.needSMSOrGoogleTfa) {
            if (fromFakePot) {
                router
                    .push(
                        getRoute(
                            ROUTE.LPC.SIGN_IN,
                            '?page=loginConfirmation&&autoCreate=true' +
                                '&&email=' +
                                values.email +
                                '&&password=' +
                                values.password +
                                '&&phone=' +
                                data.telPE +
                                '&&needSmsTfa=' +
                                data.needSmsTfa +
                                '&&needGoogleTfa=' +
                                data.needGoogleTfa,
                        ),
                    )
                    .then();
            } else {
                setPage(pages.loginConfirmation);
                setEmail(values.email);
                setPassword(values.password);
                setStayConnected(values.stayConnected);
                setPhone(data.telPE);
                if (data.needSmsTfa) {
                    setTfaType(DoubleAuthenticationModel.SMS);
                } else if (data.needGoogleTfa) {
                    setTfaType(DoubleAuthenticationModel.GOOGLE_AUTHENTICATOR);
                } else {
                    setTfaType(DoubleAuthenticationModel.BOTH);
                }
            }
        } else {
            if (getItemFromStorage('session', 'temporaryPot')) {
                fromFakePot &&
                    sendAnalytics &&
                    // @ts-ignore
                    window.follow_steps_pot_creation(
                        potCreationType == Types.LPC ? '-festif' : '-solidaire',
                        'pagecagnotte',
                    );
                return router.push(getRoute(ROUTE.LPC.POT.CREATE_POT, `?action=autoCreate`)).then();
            }

            if (query) {
                return router.push(getRoute(ROUTE.LPC.POT.POT, query, `?participate=${true}`)).then();
            }

            if (getItemFromStorage('local', 'payWithPot')) {
                return router
                    .push(getRoute(ROUTE.LPC.POT.SPENDING_PARTNER_LIMONETIK, getItemFromStorage('local', 'payWithPot')))
                    .then();
            }

            if (router.query?.source) {
                return router.push(getRoute(router.query?.source)).then();
            }
            redirectAfterLogin();
        }
    };

    const redirectAfterLogin = () => {
        if (router.query?.urlReturn) {
            // @ts-ignore
            return window.location.assign(router.query.urlReturn);
        }
        AuthService.userHasPots()
            .then((res) => {
                res.data
                    ? router.push(getRoute(ROUTE.LPC.DASHBOARD.MY_POTS_CREATED)).then()
                    : router.push(getRoute(ROUTE.LPC.POT.CREATE_POT)).then();
            })
            .catch(() => {
                router.push(getRoute(ROUTE.LPC.POT.CREATE_POT)).then();
            })
            .finally(() => setIsLoading(false));
    };

    const onSubmit = async (
        values: { email: string; password: string; stayConnected: boolean },
        setPage,
        pages,
        setEmail,
        setPassword,
        setStayConnected,
        setPhone,
        setTfaType,
    ) => {
        setIsLoading(true);
        const captcha = await recaptchaRef.current?.executeAsync();
        setError({ smsLimitSmsExceeded: false, credentialsError: false });
        AuthService.signIn({ captcha, ...values })
            .then((res) =>
                onLoginSuccess(
                    res.data,
                    values,
                    setPage,
                    pages,
                    setEmail,
                    setPassword,
                    setStayConnected,
                    setPhone,
                    setTfaType,
                ),
            )
            .catch((err) => {
                onLoginError(err);
            })
            .finally(() => {
                recaptchaRef.current?.reset();
                setIsLoading(false);
            });
    };

    useEffect(() => {
        setQuery(router.query?.potRef);
    }, [router.query?.potRef]);

    const onLoginError = (error) => {
        if (isInternalServerError(error)) {
            addToast(t(`errors.${error.code}`, { ns: 'common' }), {
                appearance: 'error',
                autoDismiss: true,
            });
            return;
        }
        const loginError: loginErrorType = {
            credentialsError: error.errors
                ? error.errors.filter((e) => e.errorField === 'password' || e.errorField === 'email')?.length > 0
                : false,
            smsLimitSmsExceeded: error.code === 'SMS_LIMIT_EXCEEDED',
        };
        if (!ObjectService.hasContent(loginError)) {
            if (error.code === 'SMS_LIMIT_SMS_EXCEEDED') {
                setIsAccountBlockedModalVisible(true);
            } else {
                addToast(t(`errors.${error.code}`, { ns: 'common' }), {
                    appearance: 'error',
                    autoDismiss: true,
                });
            }
        } else {
            setError(loginError);
        }
    };

    return (
        <AuthenticationPageContext.Consumer>
            {({ setPage, pages, setEmail, setPassword, setStayConnected, setPhone, setTfaType }) => (
                <Formik
                    validateOnBlur={false}
                    validationSchema={signIn}
                    initialValues={getInitialValue()}
                    onSubmit={(values) => {
                        event.preventDefault();
                        onSubmit(values, setPage, pages, setEmail, setPassword, setStayConnected, setPhone, setTfaType);
                    }}
                >
                    {() => (
                        <Form className={css.form}>
                            <ReCAPTCHA sitekey={process.env.RECAPTCHA_SITE_KEY} ref={recaptchaRef} size="invisible" />
                            {!fromFakePot && (
                                <div className={css.input__email}>
                                    <Input
                                        id={'email'}
                                        isFloatingLabel
                                        name="email"
                                        type="email"
                                        label={t('login.email', { count: 3 })}
                                    />
                                </div>
                            )}
                            <div className={css.input}>
                                <PasswordInput
                                    isFloatingLabel
                                    name="password"
                                    type="password"
                                    label={t('login.password')}
                                    iconPosition="right"
                                />
                                <LostPasswordLink fromFakePot={fromFakePot} />
                            </div>
                            <div className={css.checkbox}>
                                <Checkbox name="stayConnected">{t('login.remember-me')} </Checkbox>
                            </div>

                            <Button
                                customClass={css.form__cta}
                                variant="primary"
                                type="submit"
                                isLoading={isLoading}
                                isDisabled={isLoading}
                            >
                                {t('login.connect-button')}
                            </Button>
                            {isAccountBlockedModalVisible && (
                                <AccountBlockedModal
                                    isAccountBlockedModalVisible={isAccountBlockedModalVisible}
                                    onHide={() => setIsAccountBlockedModalVisible(false)}
                                />
                            )}
                        </Form>
                    )}
                </Formik>
            )}
        </AuthenticationPageContext.Consumer>
    );
};

export default LoginForm;
